import React, { useMemo, useState } from 'react';
import { useFitStore } from '../../store/useFitStore';
import type { Program, Workout, Session, WorkoutSection, PlannedExercise } from '../../store/types';

/* ============================================================
   Apply Program — drops a Library program's workouts onto the
   selected client's week. The inverse of saveWeekAsProgram:
   a flat Workout becomes a single-section Session.

   Note: the Program↔Session round-trip is lossy by design —
   programs are flat, so an applied program lands as one "Main"
   section. The coach then re-sections / tweaks in the editor.
============================================================ */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

let _uid = 0;
const uid = (p: string) => `${p}_${Date.now()}_${++_uid}`;

const fmtShort = (d: Date): string => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// Flat Workout → sectioned Session (single Main section).
const workoutToSections = (workout: Workout): WorkoutSection[] => [
  {
    id: uid('sec'),
    type: 'main',
    title: 'Main',
    exercises: workout.exercises.map((ex): PlannedExercise => ({
      id: uid('pe'),
      name: ex.name,
      supersetId: ex.supersetId,
      sets: Array.from({ length: Math.max(ex.sets, 1) }, () => ({
        reps: ex.reps || undefined,
        load: ex.weight || undefined,
      })),
    })),
  },
];

interface Props {
  clientId: string;
  clientName: string;
  coachId: string;
  weekDays: Date[];          // the 7 dates of the first visible week (Mon→Sun)
  occupiedDates: Set<string>; // dates already programmed for this client
  isoLocal: (d: Date) => string;
  onClose: () => void;
}

const ApplyProgramModal: React.FC<Props> = ({
  clientId,
  clientName,
  coachId,
  weekDays,
  occupiedDates,
  isoLocal,
  onClose,
}) => {
  const { masterPrograms, upsertSession, showToast } = useFitStore();

  const [selected, setSelected] = useState<Program | null>(null);
  // workoutId → day index (0-6) within the visible week
  const [dayByWorkout, setDayByWorkout] = useState<Record<string, number>>({});

  const pickProgram = (p: Program) => {
    setSelected(p);
    // Default: spread workouts across the week (3 → Mon/Wed/Fri, etc.)
    const n = p.workouts.length;
    const spread: Record<string, number> = {};
    p.workouts.forEach((w, i) => {
      spread[w.id] = n <= 1 ? 0 : Math.round((i * 6) / (n - 1));
    });
    setDayByWorkout(spread);
  };

  const apply = () => {
    if (!selected) return;
    let placed = 0;
    let skipped = 0;
    selected.workouts.forEach((w) => {
      const dayIdx = dayByWorkout[w.id] ?? 0;
      const date = isoLocal(weekDays[dayIdx]);
      if (occupiedDates.has(date)) {
        skipped++;
        return; // never clobber an already-programmed day
      }
      const session: Session = {
        id: crypto.randomUUID(), // top-level PK must be a real uuid (nested sec/pe ids stay strings)
        clientId,
        coachId,
        date,
        title: w.title,
        status: 'planned',
        sections: workoutToSections(w),
      };
      upsertSession(session);
      placed++;
    });
    if (placed === 0) {
      showToast('Those days are already programmed — nothing applied', 'error');
    } else {
      showToast(
        `Applied ${placed} workout${placed === 1 ? '' : 's'} to ${clientName}` +
          (skipped ? ` · skipped ${skipped} occupied day${skipped === 1 ? '' : 's'}` : '')
      );
      onClose();
    }
  };

  const weekLabel = useMemo(
    () => `${fmtShort(weekDays[0])} – ${fmtShort(weekDays[6])}`,
    [weekDays]
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="card flex flex-col" style={{ maxHeight: '86vh' }}>
          <div className="flex items-start justify-between mb-1">
            <h2 className="text-xl font-bold tracking-tight">Apply a program</h2>
            <button onClick={onClose} className="text-fit-text-secondary hover:text-white text-lg leading-none">×</button>
          </div>
          <p className="mono-label mb-4">{clientName} · week of {weekLabel}</p>

          {!selected ? (
            /* ---- step 1: pick a program ---- */
            masterPrograms.length === 0 ? (
              <p className="text-[13px] text-fit-muted py-8 text-center">
                No programs in your Library yet. Build a week and “Save as Program”, or create one in the Library.
              </p>
            ) : (
              <div className="flex flex-col gap-2 overflow-y-auto no-scrollbar">
                {masterPrograms.map((p) => {
                  const exCount = p.workouts.reduce((n, w) => n + w.exercises.length, 0);
                  return (
                    <button
                      key={p.id}
                      onClick={() => pickProgram(p)}
                      className="text-left rounded-fit-md border border-fit-line hover:border-fit-line-strong hover:bg-white/[0.03] px-4 py-3 transition-colors"
                    >
                      <p className="text-[14px] font-semibold">{p.title}</p>
                      <p className="text-[11px] text-fit-muted mt-0.5">
                        {p.workouts.length} workout{p.workouts.length === 1 ? '' : 's'} · {exCount} exercise{exCount === 1 ? '' : 's'}
                      </p>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            /* ---- step 2: assign each workout to a day ---- */
            <>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setSelected(null)} className="text-fit-text-secondary hover:text-white text-sm">← Programs</button>
                <span className="text-[14px] font-semibold truncate">{selected.title}</span>
              </div>
              <div className="flex flex-col gap-2 overflow-y-auto no-scrollbar flex-1">
                {selected.workouts.map((w) => {
                  const dayIdx = dayByWorkout[w.id] ?? 0;
                  const clash = occupiedDates.has(isoLocal(weekDays[dayIdx]));
                  return (
                    <div key={w.id} className="flex items-center justify-between gap-3 rounded-fit-md border border-fit-line px-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold truncate">{w.title}</p>
                        <p className="text-[11px] text-fit-muted">
                          {w.exercises.length} exercise{w.exercises.length === 1 ? '' : 's'}
                          {clash && <span className="text-white"> · day occupied, will skip</span>}
                        </p>
                      </div>
                      <select
                        value={dayIdx}
                        onChange={(e) => setDayByWorkout((m) => ({ ...m, [w.id]: Number(e.target.value) }))}
                        className="input shrink-0"
                        style={{ width: 150 }}
                      >
                        {weekDays.map((d, i) => (
                          <option key={i} value={i}>
                            {WEEKDAYS[i]} {d.getDate()}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end gap-2 pt-4 mt-4 border-t border-fit-line">
                <button onClick={onClose} className="btn btn-glass btn-sm">Cancel</button>
                <button onClick={apply} className="btn btn-primary btn-sm">Apply to week</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplyProgramModal;
